import React, { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { RefreshCw } from 'react-feather'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import {
  useGetCommunityUsersQuery,
  useGetTopHoldersQuery,
  useGetTopLicenceProvidersQuery,
  useRefreshLeaderboardsMutation
} from 'state/services/community/community'
import { useDispatch } from 'react-redux'
import { communityApi, CommunityTag } from 'state/services/community/community'
import ClansPanel from './components/ClansPanel'
import {
  Card,
  Identity,
  Muted,
  PowerValue,
  Rank,
  Row,
  TableState,
  shortWallet
} from './components/shared'

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const Column = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 152px 0 120px;
`

/* Tabs on the left, the refresh on the right, on one line at the same height. */
const TabsRow = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0 16px;
  flex-wrap: wrap;
`

const Tabs = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

const RefreshBox = styled(Flex)`
  align-items: center;
  gap: 10px;
`

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.cyan : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.background : theme.colors.white};
`

/**
 * Column headings.
 *
 * The rows read "5 licences   52" and the operator asked what 52 was — fairly,
 * because nothing said. A number beside a labelled number, with no label of
 * its own, is a number nobody can use.
 */
const HeadRow = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 0 8px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};
`

const HeadCell = styled(Text)`
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const HeadRank = styled(HeadCell)`
  width: 32px;
  flex-shrink: 0;
`

const HeadGrow = styled(Flex)`
  flex: 1;
  min-width: 0;
`

/**
 * A contract, and where we know it, which one.
 *
 * A holders table that lists the distributor above every person is not a
 * ranking of the community — it is our own inventory in a member's clothes.
 * Ours gets its name; anybody else's contract gets the plain fact that it has
 * code, because that is all we know about it.
 */
const Tag = styled.span<{ $ours: boolean }>`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  border: 1px solid
    ${({ theme, $ours }) => ($ours ? theme.colors.cyan : theme.colors.gray8)};
  color: ${({ theme, $ours }) => ($ours ? theme.colors.cyan : theme.colors.gray6)};
`

const Note = styled(Muted)`
  margin-top: 12px;
  display: block;
`

/**
 * How the number is arrived at, once, where anybody who wonders will look.
 *
 * The wallet that prompted this holds 5 licences and 9,459,048 MPRO: five
 * points for the licences and forty-seven for the MPRO, which is the 52 on the
 * row.
 */
const POWER_TOOLTIP =
  'One point per licence, plus one for every 200,000 MPRO held. Counted the way the DAO counts it, read live from the chain — a dash means it could not be read, which is not the same as zero.'

type TabKey = 'users' | 'holders' | 'providers' | 'clans'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'holders', label: 'Top Holders' },
  { key: 'providers', label: 'Top Licence Providers' },
  { key: 'clans', label: 'Clans' }
]

function computedNote(computedAt: string | null): string {
  if (!computedAt) return ''
  const when = new Date(computedAt)
  return `Ranking as of ${when.toLocaleString()}.`
}

const Community: React.FC = () => {
  /*
   * The tab lives in the URL.
   *
   * It used to be local state defaulting to 'users', which made the clans tab
   * unaddressable — so every clan notification could only ever land on the
   * users list, however precisely we knew what the reader was coming to do.
   * A tab in the URL is also shareable and survives the back button.
   */
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab') as TabKey | null
  const tab: TabKey = TABS.some((t) => t.key === requested) ? (requested as TabKey) : 'users'
  const setTab = useCallback(
    (next: TabKey) => {
      setParams(next === 'users' ? {} : { tab: next }, { replace: true })
    },
    [setParams]
  )

  const users = useGetCommunityUsersQuery({ limit: 50 }, { skip: tab !== 'users' })
  const holders = useGetTopHoldersQuery({ limit: 25 }, { skip: tab !== 'holders' })
  const providers = useGetTopLicenceProvidersQuery(
    { limit: 25 },
    { skip: tab !== 'providers' }
  )

  const [refreshLeaderboards, { isLoading: refreshing }] = useRefreshLeaderboardsMutation()
  const [refreshNote, setRefreshNote] = useState('')
  const dispatch = useDispatch()

  const onRefresh = async () => {
    setRefreshNote('')

    /*
     * The clans tab has nothing to recompute — its data is not a ranking, it
     * is who belongs where right now, and it goes stale because somebody else
     * acted. Invalidating the tags makes every clan query on screen refetch;
     * calling the leaderboard recompute here would be a different action
     * wearing the same button.
     */
    if (tab === 'clans') {
      dispatch(
        communityApi.util.invalidateTags([
          CommunityTag.CLAN,
          CommunityTag.MEMBERSHIP,
          CommunityTag.PENDING
        ])
      )
      return
    }

    let recomputed: boolean
    try {
      recomputed = (await refreshLeaderboards().unwrap()).recomputed
    } catch {
      setRefreshNote('Could not refresh just now')
      return
    }
    setRefreshNote(recomputed ? '' : 'Recomputed less than a minute ago')

    /*
     * Refetch only the board being looked at, and outside the block that
     * decides the message.
     *
     * The first version refetched both and did it inside the try: the inactive
     * query is skipped, RTK Query throws when you refetch one that was never
     * started, and the page reported "could not refresh just now" over a
     * refresh that had in fact just recomputed the ranking. A failure message
     * on a successful action is the same lie as a success message on a failed
     * one, and harder to notice because nothing looks broken.
     */
    try {
      if (tab === 'holders') await holders.refetch()
      if (tab === 'providers') await providers.refetch()
    } catch {
      // The ranking is recomputed; only this view is stale. The next tab
      // switch fetches it, and claiming the refresh failed would be untrue.
    }
  }

  return (
    <MainWrapper>
      {/* MainWrapper is a row Flex with no vertical offset, so a view that
          drops its content straight in gets it laid out sideways and tucked
          under the sticky header — which is exactly what this page did on its
          first deploy. 152px is the offset the DAO and Licenses pages use to
          clear the header and the promo bar above it. */}
      <Column>
        <StyledTitle>Community</StyledTitle>
      <Text variant={TextVariant.BODY_SMALL}>
        People, clans and standings.
      </Text>

      <TabsRow>
        <Tabs>
          {TABS.map((t) => (
            <Tab key={t.key} $active={tab === t.key} onClick={() => setTab(t.key)}>
              {t.label}
            </Tab>
          ))}
        </Tabs>
        {/*
          The rankings recompute on their own every ten minutes, so this button
          exists for the person who does not want to wait for the next one. It
          triggers a real recompute rather than a re-read — a refresh that only
          re-fetches the same stored answer looks broken for the nine minutes
          between scheduled runs.

          The service refuses to sweep more than once a minute, and when it
          refuses this says so instead of implying work that did not happen.
        */}
        {(tab === 'holders' || tab === 'providers' || tab === 'clans') && (
          <RefreshBox>
            <Muted variant={TextVariant.BODY_SMALL}>{refreshNote}</Muted>
            <RefreshButton disabled={refreshing} onClick={onRefresh}>
              <RefreshCw size={15} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </RefreshButton>
          </RefreshBox>
        )}
      </TabsRow>

      {tab === 'users' && (
        <Card>
          <TableState
            loading={users.isLoading}
            available={!users.isError}
            empty={(users.data?.users?.length ?? 0) === 0}
            emptyText="Nobody has made their profile public yet."
            unavailableText="Could not load the community just now."
          />
          {(users.data?.users?.length ?? 0) > 0 && (
            <HeadRow>
              <HeadGrow>
                <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>Member</HeadCell>
              </HeadGrow>
              <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>Clan</HeadCell>
              <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>
                Voting power
                <InfoIcon
                  iconProps={{ size: 13, style: { marginLeft: 5, verticalAlign: 'middle' } }}
                  tooltipProps={{ title: POWER_TOOLTIP }}
                />
              </HeadCell>
            </HeadRow>
          )}
          {users.data?.users?.map((u) => (
            <Row key={u.wallet}>
              <Identity {...u} />
              {u.clan ? (
                <Muted variant={TextVariant.BODY_SMALL}>{u.clan.name}</Muted>
              ) : null}
              <PowerValue value={u.votingPower} />
            </Row>
          ))}
        </Card>
      )}

      {tab === 'holders' && (
        <Card>
          <TableState
            loading={holders.isLoading}
            available={holders.data?.available !== false && !holders.isError}
            empty={(holders.data?.holders?.length ?? 0) === 0}
            emptyText="No balances to rank yet."
            unavailableText="The ranking has not been computed yet — check back shortly."
          />
          {(holders.data?.holders?.length ?? 0) > 0 && (
            <HeadRow>
              <HeadRank variant={TextVariant.TEXT_XS_REGULAR}>#</HeadRank>
              <HeadGrow>
                <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>Wallet</HeadCell>
              </HeadGrow>
              <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>MPRO held</HeadCell>
            </HeadRow>
          )}
          {holders.data?.holders?.map((h) => (
            <Row key={h.wallet}>
              <Rank variant={TextVariant.BODY_SMALL}>{h.rank}</Rank>
              <Identity {...h} />
              {h.isContract || h.contractName ? (
                <Tag $ours={Boolean(h.contractName)} title={h.wallet}>
                  {h.contractName ? `MPRO Lab · ${h.contractName}` : 'Smart contract'}
                </Tag>
              ) : null}
              <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
                {Number(h.mproUnits).toLocaleString()} MPRO
              </Text>
            </Row>
          ))}
          {/*
            The caveat still has to be said — this is not a chain-wide ranking
            and implying otherwise would be a claim we have not earned — but it
            belongs behind an "i", not as a paragraph under a table.
          */}
          <Note variant={TextVariant.BODY_SMALL}>
            {computedNote(holders.data?.computedAt ?? null)}
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
              tooltipProps={{
                title:
                  'Ranked among wallets MPRO Lab knows: licence holders and people with a profile here. It is not a chain-wide ranking. Addresses holding code are marked as contracts, and the platform\u2019s own are named — those are not members, they are the platform. Recomputed every ten minutes.'
              }}
            />
          </Note>
        </Card>
      )}

      {tab === 'providers' && (
        <Card>
          <TableState
            loading={providers.isLoading}
            available={providers.data?.available !== false && !providers.isError}
            empty={(providers.data?.providers?.length ?? 0) === 0}
            emptyText="No licences are held yet."
            unavailableText="The ranking has not been computed yet — check back shortly."
          />
          {(providers.data?.providers?.length ?? 0) > 0 && (
            <HeadRow>
              <HeadRank variant={TextVariant.TEXT_XS_REGULAR}>#</HeadRank>
              <HeadGrow>
                <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>Wallet</HeadCell>
              </HeadGrow>
              <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>Licences</HeadCell>
              <HeadCell variant={TextVariant.TEXT_XS_REGULAR}>
                Voting power
                <InfoIcon
                  iconProps={{ size: 13, style: { marginLeft: 5, verticalAlign: 'middle' } }}
                  tooltipProps={{ title: POWER_TOOLTIP }}
                />
              </HeadCell>
            </HeadRow>
          )}
          {providers.data?.providers?.map((p) => (
            <Row key={p.wallet}>
              <Rank variant={TextVariant.BODY_SMALL}>{p.rank}</Rank>
              <Identity {...p} />
              <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
                {p.licenceCount} {p.licenceCount === 1 ? 'licence' : 'licences'}
              </Text>
              <PowerValue value={p.votingPower} />
            </Row>
          ))}
          {/*
            No second tooltip here: the Voting Power heading already carries
            the explanation, and saying it twice in one table is how a
            explanation stops being read at all.
          */}
          <Note variant={TextVariant.BODY_SMALL}>
            {computedNote(providers.data?.computedAt ?? null)}
          </Note>
        </Card>
      )}

        {tab === 'clans' && <ClansPanel />}
      </Column>
    </MainWrapper>
  )
}

export default Community
